import Card from "@/components/card"
import Tooltip from "@/components/tooltip"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import siteStore from "@/entities/site/model/site.store"
import EstablishmentSiteImageUpload from "./EstablishmentSiteImageUpload"
import mapStore from "@/entities/map/model/map.store"
import { MapOverviewArea, MapOverviewPoint } from "@/entities/map/model/map.types"
import {
  getOverviewCoverMetrics,
  normalizeOverviewPoint,
  projectOverviewPoint
} from "@/entities/map/model/map-overview.utils"
import { getMapImageVersion } from "@/entities/map/model/map-image-cache.utils"
import MapChip from "@/entities/map/ui/MapChip"
import OverviewWarpedMapImage from "@/entities/map/ui/OverviewWarpedMapImage"
import env from "@/env"
import { faExpand, faGripVertical, faMinus, faPlus, IconDefinition } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dispatch, DragEvent, PointerEvent, SetStateAction, useEffect, useMemo, useRef, useState } from "react"
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch"
import { toast } from "react-toastify"

type AreaPointKey = keyof MapOverviewArea

const AREA_HALF_WIDTH = 0.1
const AREA_HALF_HEIGHT = 0.08
const AREA_POINT_KEYS: AreaPointKey[] = ["p1", "p2", "p3", "p4"]

interface Props {
  draftAreas: Record<string, MapOverviewArea | null>
  setDraftAreas: Dispatch<SetStateAction<Record<string, MapOverviewArea | null>>>
  editAction: "validate" | null
  onEditActionHandled: () => void
  onSaved?: () => void
  setSelectedMapId: Dispatch<SetStateAction<string | null>>
}

export default function EstablishmentSiteImage({
  draftAreas,
  setDraftAreas,
  editAction,
  onEditActionHandled,
  onSaved,
  setSelectedMapId
}: Props) {
  const dispatch = useAppDispatch()
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const maps = useAppSelector(mapStore.selectors.list)
  const sites = useAppSelector(siteStore.selectors.list)
  const [imageVersion, setImageVersion] = useState(() => Date.now())
  const [draggedPoint, setDraggedPoint] = useState<{ mapId: string; key: AreaPointKey } | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  const imageContentRef = useRef<HTMLDivElement | null>(null)

  const imageUrl = `${env.REACT_APP_URL_SPACE}/Establishments/${establishment.id}.png?v=${imageVersion}`

  useEffect(() => {
    const content = imageContentRef.current
    if (!content) return

    const updateSize = () => setViewportSize({ width: content.offsetWidth, height: content.offsetHeight })
    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(content)
    return () => observer.disconnect()
  }, [establishment.siteImageUploaded])

  const coverMetrics = useMemo(() => getOverviewCoverMetrics(viewportSize, imageSize), [imageSize, viewportSize])
  const mapRiskColors = useMemo(() => {
    const colors = new Map<string, { value: number; color: string }>()
    sites.forEach((site) => {
      if (!site.MapId || !site.cRiskMax) return
      const value = site.cRiskMax.cValueEffective ?? 0
      const current = colors.get(site.MapId)
      if (!current || value > current.value) {
        colors.set(site.MapId, { value, color: site.cRiskMax.cValueEffectiveColor ?? "#94a3b8" })
      }
    })
    return colors
  }, [sites])

  function ControlButton(icon: IconDefinition, onClick: () => void, text: string) {
    return (
      <Tooltip text={text} position="left">
        <button
          type="button"
          onClick={() => onClick()}
          className="h-9 w-9 cursor-pointer rounded-full bg-white shadow-sm hover:bg-slate-50"
          style={{ border: "1px solid #e2e8f0" }}>
          <FontAwesomeIcon icon={icon} />
        </button>
      </Tooltip>
    )
  }

  function getImagePoint(clientX: number, clientY: number): MapOverviewPoint | null {
    const content = imageContentRef.current
    if (!content || !coverMetrics) return null

    const transformedBounds = content.getBoundingClientRect()
    if (!transformedBounds.width || !transformedBounds.height) return null

    const xInViewport = ((clientX - transformedBounds.left) / transformedBounds.width) * viewportSize.width
    const yInViewport = ((clientY - transformedBounds.top) / transformedBounds.height) * viewportSize.height

    return normalizeOverviewPoint({ x: xInViewport, y: yInViewport }, coverMetrics)
  }

  function createDefaultArea(point: MapOverviewPoint): MapOverviewArea {
    const left = Math.min(1 - AREA_HALF_WIDTH * 2, Math.max(0, point.x - AREA_HALF_WIDTH))
    const top = Math.min(1 - AREA_HALF_HEIGHT * 2, Math.max(0, point.y - AREA_HALF_HEIGHT))
    const right = left + AREA_HALF_WIDTH * 2
    const bottom = top + AREA_HALF_HEIGHT * 2

    return {
      p1: { x: left, y: top },
      p2: { x: right, y: top },
      p3: { x: right, y: bottom },
      p4: { x: left, y: bottom }
    }
  }

  function handleLevelDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const mapId = event.dataTransfer.getData("text/map-id")
    const point = getImagePoint(event.clientX, event.clientY)
    if (!mapId || !point) return

    setDraftAreas((current) => ({ ...current, [mapId]: createDefaultArea(point) }))
    setSelectedMapId(mapId)
  }

  function handlePointMove(event: PointerEvent<HTMLDivElement>) {
    if (!draggedPoint) return
    const point = getImagePoint(event.clientX, event.clientY)
    if (!point) return

    setDraftAreas((current) => {
      const map = maps.find((item) => item.id === draggedPoint.mapId)
      const area = Object.prototype.hasOwnProperty.call(current, draggedPoint.mapId)
        ? current[draggedPoint.mapId]
        : map?.overviewArea
      if (!area) return current

      return {
        ...current,
        [draggedPoint.mapId]: { ...area, [draggedPoint.key]: point }
      }
    })
  }

  function toViewportPoint(point: MapOverviewPoint) {
    if (!coverMetrics) return { x: 0, y: 0 }
    return projectOverviewPoint(point, coverMetrics)
  }

  async function saveAreas() {
    setIsSaving(true)
    try {
      const results = await Promise.all(
        Object.entries(draftAreas).map(async ([mapId, overviewArea]) => {
          const map = maps.find((item) => item.id === mapId)
          if (!map) return { mapId, updatedMap: null, response: null }
          const mapWithArea = { ...map, overviewArea }
          return { mapId, updatedMap: mapWithArea, response: await mapStore.endpoints.update(mapWithArea) }
        })
      )

      const failedMapIds = results.filter(({ response }) => !response || response.error).map(({ mapId }) => mapId)
      results.forEach(({ response, updatedMap }) => {
        if (response && !response.error && updatedMap) dispatch(mapStore.actions.replace(updatedMap))
      })

      if (failedMapIds.length > 0) {
        setDraftAreas((current) =>
          Object.fromEntries(Object.entries(current).filter(([mapId]) => failedMapIds.includes(mapId)))
        )
        toast("Certains niveaux n'ont pas pu être positionnés.", { type: "error" })
        return
      }

      setDraftAreas({})
      onSaved?.()
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    if (!editAction || isSaving) return

    void saveAreas()

    onEditActionHandled()
    // The action is an imperative signal from the top navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAction])

  function getDisplayedArea(mapId: string) {
    if (Object.prototype.hasOwnProperty.call(draftAreas, mapId)) return draftAreas[mapId]
    return maps.find((map) => map.id === mapId)?.overviewArea ?? null
  }

  const unpositionedMaps = maps.filter((map) => !getDisplayedArea(map.id))

  function startDraggingMap(event: DragEvent, mapId: string) {
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/map-id", mapId)
  }

  return (
    <div className="flex min-h-0 flex-1 gap-20">
      <aside className="w-[240px] shrink-0 overflow-hidden p-15">
        <div className="mb-15">Niveaux à positionner</div>
        {unpositionedMaps.length ? (
          <div className="flex flex-col gap-10">
            {unpositionedMaps.map((map) => (
              <div
                key={map.id}
                draggable
                onDragStart={(event) => startDraggingMap(event, map.id)}
                className="flex cursor-grab items-center gap-10 overflow-hidden rounded-[12px] bg-white shadow-sm _hover"
                style={{ border: "1px solid #e2e8f0" }}>
                <img
                  className="h-[54px] w-[72px] shrink-0 object-cover"
                  src={`${env.REACT_APP_URL_SPACE}/Maps/${map.id}.png?v=${getMapImageVersion(map.id)}`}
                  alt={map.name}
                  draggable={false}
                />
                <FontAwesomeIcon className="grey-t" icon={faGripVertical} />
                <span className="min-w-0 flex-1 truncate pr-10 font-bold">{map.name}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grey-t">Tous les niveaux sont positionnés.</div>
        )}
      </aside>

      {establishment.siteImageUploaded ? (
        <Card isWithoutPadding className="relative mb-0 min-w-0 flex-1 overflow-hidden">
          <div
            className="absolute h-full w-full min-w-0 overflow-hidden rounded-[12px] bg-white"
            style={{ border: "1px solid lightgrey" }}
            onDragOver={(event) => {
              event.preventDefault()
              event.dataTransfer.dropEffect = "move"
            }}
            onDrop={handleLevelDrop}
            onPointerMove={handlePointMove}
            onPointerUp={() => setDraggedPoint(null)}
            onPointerCancel={() => setDraggedPoint(null)}>
            <TransformWrapper
              key={imageVersion}
              initialScale={1}
              minScale={0.5}
              maxScale={6}
              centerOnInit
              wheel={{ step: 0.12 }}
              pinch={{ step: 5 }}
              doubleClick={{ mode: "zoomIn" }}
              panning={{ velocityDisabled: true, disabled: draggedPoint !== null }}>
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <div className="absolute left-3 top-3 z-20 flex flex-col gap-2">
                    {ControlButton(faPlus, zoomIn, "Zoom avant")}
                    {ControlButton(faMinus, zoomOut, "Zoom arrière")}
                    {ControlButton(faExpand, resetTransform, "Retour à la vue initiale")}
                  </div>

                  <TransformComponent
                    wrapperStyle={{ width: "100%", height: "100%" }}
                    contentStyle={{ width: "100%", height: "100%" }}>
                    <div ref={imageContentRef} className="relative h-full w-full">
                      <img
                        className="block h-full w-full select-none object-cover"
                        src={imageUrl}
                        alt="Vue d'ensemble de l'établissement"
                        draggable={false}
                        onLoad={(event) =>
                          setImageSize({
                            width: event.currentTarget.naturalWidth,
                            height: event.currentTarget.naturalHeight
                          })
                        }
                      />

                      {coverMetrics &&
                        maps.map((map) => {
                          const area = getDisplayedArea(map.id)
                          if (!area) return null
                          const points = AREA_POINT_KEYS.map((key) => toViewportPoint(area[key])) as [
                            MapOverviewPoint,
                            MapOverviewPoint,
                            MapOverviewPoint,
                            MapOverviewPoint
                          ]
                          return (
                            <OverviewWarpedMapImage
                              key={map.id}
                              width={viewportSize.width}
                              height={viewportSize.height}
                              points={points}
                              src={`${env.REACT_APP_URL_SPACE}/Maps/${map.id}.png?v=${getMapImageVersion(map.id)}`}
                            />
                          )
                        })}

                      {coverMetrics && (
                        <svg
                          className="absolute inset-0 h-full w-full"
                          viewBox={`0 0 ${viewportSize.width} ${viewportSize.height}`}>
                          {maps.map((map) => {
                            const area = getDisplayedArea(map.id)
                            if (!area) return null
                            const points = AREA_POINT_KEYS.map((key) => toViewportPoint(area[key]))
                            const xs = points.map((point) => point.x)
                            const ys = points.map((point) => point.y)
                            const centerX = xs.reduce((sum, value) => sum + value, 0) / xs.length
                            const centerY = ys.reduce((sum, value) => sum + value, 0) / ys.length
                            const isEditing = Boolean(draftAreas[map.id])
                            const riskColor = mapRiskColors.get(map.id)?.color ?? "#94a3b8"

                            return (
                              <g key={map.id}>
                                <polygon
                                  points={points.map((point) => `${point.x},${point.y}`).join(" ")}
                                  fill={riskColor}
                                  fillOpacity={0.08}
                                  stroke={riskColor}
                                  strokeWidth={isEditing ? 3 : 2}
                                  className="cursor-pointer"
                                  style={{ pointerEvents: "auto" }}
                                  onPointerDown={() => {
                                    setSelectedMapId(map.id)
                                    if (!draftAreas[map.id] && map.overviewArea) {
                                      setDraftAreas((current) => ({ ...current, [map.id]: map.overviewArea! }))
                                    }
                                  }}
                                />
                                <foreignObject
                                  x={centerX - 100}
                                  y={centerY - 18}
                                  width={200}
                                  height={36}
                                  className="pointer-events-none overflow-visible">
                                  <div className="flex h-full items-center justify-center">
                                    <MapChip color={riskColor} name={map.name} />
                                  </div>
                                </foreignObject>

                                {isEditing &&
                                  AREA_POINT_KEYS.map((key, index) => (
                                    <circle
                                      key={key}
                                      cx={points[index].x}
                                      cy={points[index].y}
                                      r={7}
                                      fill="white"
                                      stroke={riskColor}
                                      strokeWidth={3}
                                      className="cursor-move"
                                      style={{ pointerEvents: "auto" }}
                                      onPointerDown={(event) => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        setDraggedPoint({ mapId: map.id, key })
                                      }}
                                    />
                                  ))}
                              </g>
                            )
                          })}
                        </svg>
                      )}
                    </div>
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
        </Card>
      ) : (
        <Card className="flex flex-1 items-center justify-center p-40">
          <div className="w-full max-w-[720px]">
            <EstablishmentSiteImageUpload onUploaded={() => setImageVersion(Date.now())} />
          </div>
        </Card>
      )}
    </div>
  )
}
